import React, { useState, useEffect } from 'react';
import { Link, useLocation as useRouterLocation } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, MapPin, ChevronDown } from 'lucide-react';
import { useLocation } from '../../contexts/LocationContext';
import { useCart } from '../../contexts/CartContext';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { selectedLocation, setShowLocationPicker, plzRouting } = useLocation();
  const { itemCount, total } = useCart();
  const routerLocation = useRouterLocation();

  const isHomePage = routerLocation.pathname === '/';
  const isAdminRoute = routerLocation.pathname.startsWith('/admin');

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [routerLocation]);

  if (isAdminRoute) return null;

  // On homepage: transparent navbar until scrolled
  // On other pages: always solid
  const isTransparent = isHomePage && !isScrolled;

  // On inner pages: always show ZOZO text (even scrolled), since there's no Hero
  // On homepage: hide ZOZO text when scrolled (Hero already established brand)
  const showBrandText = !isHomePage || !isScrolled;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isTransparent
            ? ''
            : 'navbar-solid'
        }`}
        style={{ height: isScrolled ? 60 : 72 }}
        data-testid="main-navbar"
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-between">
          {/* Left: Logo */}
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
            <img
              src="/images/logo-icon.png"
              alt="ZOZO Burger"
              className={`object-contain transition-all duration-300 ${
                isScrolled ? 'w-8 h-8' : 'w-10 h-10'
              }`}
            />
            {/* Brand text - Desktop only, conditional on page */}
            <span
              className={`hidden md:block font-display font-bold text-white tracking-wide transition-all duration-300 ${
                showBrandText
                  ? 'opacity-100 max-w-[80px]'
                  : 'opacity-0 max-w-0 overflow-hidden'
              } ${isScrolled ? 'text-lg' : 'text-xl'}`}
            >
              ZOZO
            </span>
          </Link>

          {/* Center: Navigation (Desktop) */}
          <div className="hidden md:flex items-center gap-1">
            <NavLink to="/bestellen" label="Speisekarte" active={routerLocation.pathname.startsWith('/bestellen')} />
            <button
              onClick={() => setShowLocationPicker(true)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                plzRouting && selectedLocation
                  ? 'text-white/60 hover:text-white hover:bg-white/5'
                  : 'text-primary-500 bg-primary-500/10 hover:bg-primary-500/15 animate-subtle-pulse'
              }`}
              data-testid="location-selector"
            >
              <MapPin className="w-3.5 h-3.5" />
              <span className="max-w-[140px] truncate">
                {plzRouting && selectedLocation ? selectedLocation.name?.replace('ZOZO Burger ', '') : 'Standort wählen'}
              </span>
              <ChevronDown className="w-3 h-3 opacity-50" />
            </button>
          </div>

          {/* Right: Actions (Desktop) */}
          <div className="hidden md:flex items-center gap-2">
            <Link
              to="/admin"
              className="p-2 rounded-lg hover:bg-white/5 transition-colors text-white/30 hover:text-white/60"
              aria-label="Admin"
              data-testid="admin-link"
            >
              <User className="w-[18px] h-[18px]" />
            </Link>

            <Link
              to="/bestellen"
              className="relative flex items-center gap-2 ml-1.5 px-5 py-2.5 bg-primary-500 hover:bg-primary-700 text-white rounded-lg font-semibold text-sm transition-all duration-200 hover:shadow-[0_4px_14px_0_rgba(168,28,28,0.35)] hover:-translate-y-px"
              data-testid="cart-button"
            >
              <ShoppingCart className="w-[18px] h-[18px]" />
              <span>€{total.toFixed(2).replace('.', ',')}</span>
              {itemCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-amber-400 text-neutral-900 text-[10px] font-bold rounded-full flex items-center justify-center shadow-sm">
                  {itemCount}
                </span>
              )}
            </Link>
          </div>

          {/* Mobile Actions */}
          <div className="flex md:hidden items-center gap-0.5">
            <Link to="/bestellen" className="relative p-2.5 text-white/80" data-testid="mobile-cart-button">
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute top-0.5 right-0 w-4 h-4 bg-primary-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2.5 text-white/80 hover:text-white transition-colors"
              aria-label={isMobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Fullscreen Menu */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-all duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="absolute inset-0 bg-neutral-950/90 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
        <div className={`absolute top-[72px] left-0 right-0 bg-neutral-900/98 border-b border-white/5 transition-transform duration-300 ${
          isMobileMenuOpen ? 'translate-y-0' : '-translate-y-4'
        }`}>
          <div className="container mx-auto px-4 py-5 space-y-1">
            <MobileNavLink to="/bestellen" label="Speisekarte" onClick={() => setIsMobileMenuOpen(false)} />
            <button
              onClick={() => { setShowLocationPicker(true); setIsMobileMenuOpen(false); }}
              className={`flex items-center gap-3 w-full text-left py-3 px-3 rounded-lg transition-all ${
                plzRouting && selectedLocation
                  ? 'text-white/70 hover:text-white hover:bg-white/5'
                  : 'text-primary-500 bg-primary-500/8'
              }`}
            >
              <MapPin className="w-5 h-5" />
              <span className="font-medium">{(plzRouting && selectedLocation?.name) || 'Standort wählen'}</span>
            </button>
            <MobileNavLink to="/admin" label="Admin" onClick={() => setIsMobileMenuOpen(false)} muted />
          </div>
        </div>
      </div>
    </>
  );
}

function NavLink({ to, label, active }) {
  return (
    <Link
      to={to}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
        active ? 'text-white bg-white/8' : 'text-white/60 hover:text-white hover:bg-white/5'
      }`}
    >
      {label}
    </Link>
  );
}

function MobileNavLink({ to, label, onClick, muted }) {
  return (
    <Link to={to} onClick={onClick}
      className={`flex items-center py-3 px-3 rounded-lg font-medium transition-all ${
        muted ? 'text-white/30 hover:text-white/50 hover:bg-white/5' : 'text-white/80 hover:text-white hover:bg-white/5 text-lg'
      }`}
    >
      {label}
    </Link>
  );
}
